import { Box, Card, Stack } from '@mui/material';
import { colors, Typography, Tooltip } from '@sweep-io/sweep-design';
import { Globe, Users } from '@sweep-io/sweep-design/dist/icons';
import { Artifact, ArtifactCardVariant } from '../../../types/artifactsTypes';
import { ArtifactCardIllustration } from './ArtifactCardIllustration';
import { humanizeDate, humanizeDateVariants } from '../../helpers/humanizeDate';
import { stripQuotes } from '../../../lib/stripQuotes';
import { TruncatedTextTooltip } from '../../common/TruncatedTextTooltip';
import { UserInfo } from '../../../types/UserInfoTypes';

interface ArtifactCardProps {
  artifact: Artifact;
  variant: ArtifactCardVariant;
  onClick: () => void;
  usersById: Record<string, UserInfo>;
}

export const ArtifactCard = ({ artifact, variant, onClick, usersById }: ArtifactCardProps) => {
  const createdByName = usersById[artifact.createdById]?.name || 'Unknown';

  const formattedDate = humanizeDate({
    dateOrTimestamp: artifact.createdAt,
    variant: humanizeDateVariants.SHORT_WITHOUT_TIME,
  });

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        borderRadius: '8px',
        border: `1px solid ${colors.grey[300]}`,
        boxShadow: 'none',
        transition: 'border-color 0.2s ease-in-out',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: 'white',
        '&:hover': {
          borderColor: colors.blue[500],
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          padding: 2,
          minHeight: '120px',
          pb: 0,
        }}
      >
        <ArtifactCardIllustration />
      </Box>

      <Stack p={2} gap={0.5} sx={{ boxShadow: '0px 2px 12px 0px rgba(0, 0, 0, 0.1)' }}>
        <TruncatedTextTooltip title={stripQuotes(artifact.title)}>
          <Typography variant="body-medium" color={colors.grey[900]}>
            {stripQuotes(artifact.title)}
          </Typography>
        </TruncatedTextTooltip>

        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="caption" color={colors.grey[500]}>
            {variant === 'shared' ? (
              <>
                Created on {formattedDate} by {createdByName}
              </>
            ) : (
              <>Created on {formattedDate}</>
            )}
          </Typography>

          {/* Status icons - only show if there are icons to display */}
          {(artifact.isPublic || (variant === 'my-artifacts' && artifact.isShared)) && (
            <>
              <Typography variant="caption" color={colors.grey[500]}>
                |
              </Typography>
              <Stack direction="row" alignItems="center" gap={1}>
                {artifact.isPublic && (
                  <Tooltip title="Published: Public link enabled" placement="top">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Globe size="medium" color={colors.grey[900]} />
                    </Box>
                  </Tooltip>
                )}
                {variant === 'my-artifacts' && artifact.isShared && (
                  <Tooltip title="Shared with your company" placement="top">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Users size="medium" color={colors.grey[900]} />
                    </Box>
                  </Tooltip>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};
